/**
 * Modal per assegnare impianti a un pianificatore
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Checkbox,
  Chip,
  InputAdornment,
  Alert,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { plantsApi, plannersApi } from '../../services/api';

// Definizione colonne
const columns = [
  { id: 'plant_code', label: 'Cod. Impianto', width: 120 },
  { id: 'delivery_code', label: 'Cod. Consegna', width: 120 },
  { id: 'indirizzo', label: 'Indirizzo', width: 200 },
  { id: 'citta', label: 'Città', width: 150 },
  { id: 'provincia', label: 'Provincia', width: 80 }
];

const AssignPlantsModal = ({ open, onClose, planner, onSuccess }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlantIds, setSelectedPlantIds] = useState([]);
  const [orderBy, setOrderBy] = useState('delivery_code');
  const [orderDir, setOrderDir] = useState('asc');

  // Carica tutti gli impianti
  const plantsQuery = useQuery({
    queryKey: ['allPlants'],
    queryFn: () => plantsApi.getAll({ limit: 1000 }),
    enabled: open,
    staleTime: 60000
  });

  // Inizializza selezione con impianti già assegnati
  useEffect(() => {
    if (planner?.assignedPlants) {
      setSelectedPlantIds(planner.assignedPlants.map(p => p.id));
    } else {
      setSelectedPlantIds([]);
    }
  }, [planner, open]);

  // Mutation per assegnare impianti
  const assignMutation = useMutation({
    mutationFn: ({ plannerId, plantIds }) => {
      console.log('🚀 Assigning plants:', { plannerId, plantIds });
      return plannersApi.assignPlants(plannerId, plantIds);
    },
    onSuccess: (response) => {
      console.log('✅ Assignment success:', response);
      queryClient.invalidateQueries(['planners']);
      onSuccess?.();
      onClose();
    },
    onError: (error) => {
      console.error('❌ Assignment error:', error);
    }
  });

  // Filtra e ordina impianti
  const sortedPlants = useMemo(() => {
    const allPlants = plantsQuery.data?.data?.data || [];
    
    // Filtra
    let filtered = allPlants;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = allPlants.filter(p => 
        p.plant_code?.toLowerCase().includes(term) ||
        p.delivery_code?.toLowerCase().includes(term) ||
        p.nome?.toLowerCase().includes(term) ||
        p.indirizzo?.toLowerCase().includes(term) ||
        p.citta?.toLowerCase().includes(term) ||
        p.provincia?.toLowerCase().includes(term)
      );
    }
    
    // Ordina
    return [...filtered].sort((a, b) => {
      const aVal = a[orderBy] || '';
      const bVal = b[orderBy] || '';
      
      // Gestisci numeri
      if (orderBy === 'id') {
        return orderDir === 'asc' ? a.id - b.id : b.id - a.id;
      }
      
      // Stringhe
      const comparison = String(aVal).localeCompare(String(bVal), 'it', { numeric: true });
      return orderDir === 'asc' ? comparison : -comparison;
    });
  }, [plantsQuery.data, searchTerm, orderBy, orderDir]);

  // Gestione ordinamento
  const handleSort = (columnId) => {
    if (orderBy === columnId) {
      setOrderDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(columnId);
      setOrderDir('asc');
    }
  };

  // Toggle selezione impianto
  const togglePlant = (plantId) => {
    setSelectedPlantIds(prev => 
      prev.includes(plantId)
        ? prev.filter(id => id !== plantId)
        : [...prev, plantId]
    );
  };

  // Seleziona/deseleziona tutti visibili
  const toggleAll = () => {
    const visibleIds = sortedPlants.map(p => p.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedPlantIds.includes(id));
    
    if (allSelected) {
      setSelectedPlantIds(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      setSelectedPlantIds(prev => [...new Set([...prev, ...visibleIds])]);
    }
  };

  const handleSubmit = () => {
    console.log('📝 handleSubmit called, planner:', planner, 'selectedPlantIds:', selectedPlantIds);
    if (!planner?.id) {
      console.error('❌ No planner ID!');
      return;
    }
    assignMutation.mutate({
      plannerId: planner.id,
      plantIds: selectedPlantIds
    });
  };

  // Conta impianti già assegnati ad altri pianificatori
  const getOtherPlannerName = (plant) => {
    if (!plant.planner_id || plant.planner_id === planner?.id) return null;
    return plant.planner?.name || `Pianificatore #${plant.planner_id}`;
  };

  const allVisibleSelected = sortedPlants.length > 0 && sortedPlants.every(p => selectedPlantIds.includes(p.id));
  const someVisibleSelected = sortedPlants.some(p => selectedPlantIds.includes(p.id)) && !allVisibleSelected;

  if (!planner) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PersonIcon color="primary" />
        Assegna Impianti a {planner.name}
      </DialogTitle>
      
      <DialogContent dividers>
        {/* Barra di ricerca */}
        <TextField
          fullWidth
          placeholder="Cerca impianti per codice, indirizzo, città, provincia..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          sx={{ mb: 2 }}
        />

        {/* Info selezione */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {selectedPlantIds.length} impianti selezionati su {sortedPlants.length} visualizzati
          </Typography>
          <Button size="small" onClick={toggleAll}>
            {allVisibleSelected ? 'Deseleziona tutti' : 'Seleziona tutti visibili'}
          </Button>
        </Box>

        <Divider sx={{ mb: 1 }} />

        {/* Tabella impianti */}
        {plantsQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : sortedPlants.length === 0 ? (
          <Alert severity="info">
            Nessun impianto trovato
          </Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 450 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ bgcolor: 'background.paper' }}>
                    <Checkbox
                      indeterminate={someVisibleSelected}
                      checked={allVisibleSelected}
                      onChange={toggleAll}
                    />
                  </TableCell>
                  {columns.map((col) => (
                    <TableCell 
                      key={col.id}
                      sx={{ 
                        fontWeight: 'bold', 
                        bgcolor: 'background.paper',
                        width: col.width
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === col.id}
                        direction={orderBy === col.id ? orderDir : 'asc'}
                        onClick={() => handleSort(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>
                    Assegnato a
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedPlants.map((plant) => {
                  const isSelected = selectedPlantIds.includes(plant.id);
                  const otherPlanner = getOtherPlannerName(plant);
                  
                  return (
                    <TableRow
                      key={plant.id}
                      hover
                      onClick={() => togglePlant(plant.id)}
                      selected={isSelected}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox checked={isSelected} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        {plant.plant_code || '-'}
                      </TableCell>
                      <TableCell>
                        {plant.delivery_code || '-'}
                      </TableCell>
                      <TableCell>{plant.indirizzo || '-'}</TableCell>
                      <TableCell>{plant.citta || '-'}</TableCell>
                      <TableCell>{plant.provincia || '-'}</TableCell>
                      <TableCell>
                        {otherPlanner ? (
                          <Chip 
                            label={otherPlanner}
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        ) : isSelected && planner?.id === plant.planner_id ? (
                          <Chip 
                            label="Questo"
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {assignMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Errore nell'assegnazione: {assignMutation.error?.response?.data?.message || 'Errore sconosciuto'}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={assignMutation.isLoading}>
          Annulla
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={assignMutation.isLoading}
        >
          {assignMutation.isLoading ? 'Salvataggio...' : `Assegna ${selectedPlantIds.length} impianti`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignPlantsModal;
